import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AppSettingsService } from '@services/app-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd, ActivatedRoute, NavigationStart } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, map, mergeMap } from 'rxjs';

import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { FA_ICONS } from './core/config/fa-icons';

import { SHARED_MODULES, SHARED_TUAIN_FORMS } from './shared/shared-standalone';

import { NzModalService } from 'ng-zorro-antd/modal';

import { ThemeService } from './core/services/theme.service';
import { EventManagerService } from '@services/event-manager.service';
import { UserSessionService } from '@services/user-session.service';
import { AuthService } from '@services/auth.service';
import { RestoredPageSessionService } from '@services/restored-page-session.service';
import { GoogleMapsLoaderService } from './core/services/google-maps-loader.service';
import { AppLoggerService } from '@services/app-logger.service';
import { AnalyticsTrackerService } from '@services/analytics-tracker.service';
import { GtmAnalyticsService } from '@services/gtm-analytics.service';
import { VersionService } from '@services/version.service';
import { MaintenanceGuardService } from '@services/maintenance-guard.service';

import { defaultConfig } from 'src/app/app.config';

const { sessionToBeClosedWarning } = defaultConfig;

const {
  appStarted,
  closeInvasiveNotification,
  enableNotifications,
  invasiveNotification,
  keepTheSessionAlive,
  sessionClosedByServer,
  sessionEnded,
  sessionReplaced,
  sessionToBeClosed,
} = defaultConfig.appEvents;

import english from './locales/en-US';
import french from './locales/fr-FR';
import russian from './locales/ru-RU';
import chinese from './locales/zh-CN';

const { warningDuration: _WARNING_DURATION } = defaultConfig;

const NOTIFICATION_VIEW_TIME = 300000;
const _locales = {
  'en-US': english,
  'fr-FR': french,
  'ru-RU': russian,
  'zh-CN': chinese,
};

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [...SHARED_MODULES, ...SHARED_TUAIN_FORMS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nz-layout>
      <nz-content>
        <router-outlet></router-outlet>
      </nz-content>
    </nz-layout>
    @if (version.label) {
      <div style="position:fixed; bottom:4px; right:8px; font-size:11px; opacity:.5; pointer-events:none; z-index:1000">
        {{ version.label }}
      </div>
    }
  `,
})
export class AppComponent implements OnInit {
  private readonly _cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private titleService = inject(Title);
  private _settings = inject(AppSettingsService);
  private _eventManager = inject(EventManagerService);
  private _userSession = inject(UserSessionService);
  private _authService = inject(AuthService);
  private _restoredPageSession = inject(RestoredPageSessionService);
  private _googleMapsLoader = inject(GoogleMapsLoaderService);
  private modal = inject(NzModalService);
  private themeService = inject(ThemeService);

  private destroyRef = inject(DestroyRef);
  private logger = inject(AppLoggerService);
  private analyticsTracker = inject(AnalyticsTrackerService);
  private gtmAnalytics = inject(GtmAnalyticsService);
  private _maintenanceGuard = inject(MaintenanceGuardService);
  version = inject(VersionService);
  _locale: string;
  _theme: string;
  currentModal: any = null;
  sessionExpirationTimer: ReturnType<typeof setTimeout> = null;
  autoCloseTimeout: ReturnType<typeof setTimeout> = null;
  inClosing = false;
  isCollapsed = false;
  notification: any = {};
  viewNotification = false;
  notificationEnabled = false;

  constructor() {
    const library = inject(FaIconLibrary);

    library.addIcons(...FA_ICONS);
  }

  async ngOnInit() {
    // GA4 vía GTM (guía GrowData): carga el contenedor y trackea page_view por ruta.
    // No-op si environment.gtmId está vacío (dev/local).
    this.gtmAnalytics.init();
    this._googleMapsLoader
      .loadGoogleMaps()
      .then(() => {
        this.logger.debug('Google Maps API cargada');
      })
      .catch(err => {
        this.logger.warn('[App] No se pudo cargar Google Maps API:', err);
      });

    // set page title from router data variable
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        mergeMap(route => route.data),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        this.titleService.setTitle(defaultConfig.applicationName + ' | ' + event['title']);
        const title = event['title'];
        if (title && this._userSession.getSessionData()?.sessionId) {
          this.analyticsTracker.trackFeatureOpened(String(title).toUpperCase().replace(/\s+/g, '_') + '_OPENED');
        }
      });

    // listen url query params and set them to ngrx store
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationStart) => {
        const queryString = event.url.match(/\?(.*)/);
        if (queryString) {
          const queryParams = Object.fromEntries(new URLSearchParams(queryString[1]));
          const keys = Object.keys(queryParams);
          if (keys.length) {
            keys.forEach(key => {
              let value;
              switch (queryParams[key]) {
                case 'false':
                  value = false;
                  break;
                case 'true':
                  value = true;
                  break;
                default:
                  value = queryParams[key];
                  break;
              }
              this._settings.setKey(key, value);
            });
          }
        }
      });

    // detecting & set mobile/tablet/desktop viewports
    const setViewPort = (isMobileView: any = false, isTabletView: any = false) => {
      this._settings.set({ isMobileView });
      this._settings.set({ isTabletView });
    };
    const detectViewPort = (load = false) => {
      const _isMobileView = window.innerWidth < 768;
      const _isTabletView = window.innerWidth < 992;
      const _isDesktopView = !_isMobileView && !_isTabletView;
      const isMobileView = JSON.parse(window.localStorage.getItem('app.settings.isMobileView'));
      const isTabletView = JSON.parse(window.localStorage.getItem('app.settings.isTabletView'));
      const isDesktopView = !isMobileView && !isTabletView;
      if (_isDesktopView && (_isDesktopView !== isDesktopView || load)) {
        setViewPort(false, false);
      }
      if (_isTabletView && !_isMobileView && (_isTabletView !== isTabletView || load)) {
        setViewPort(false, true);
        this._settings.set({ isMenuCollapsed: true });
      }
      if (_isMobileView && (_isMobileView !== isMobileView || load)) {
        setViewPort(true, false);
      }
    };
    detectViewPort(true);
    const resizeHandler = () => detectViewPort();
    window.addEventListener('resize', resizeHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', resizeHandler));

    // set primary color on app load
    const primaryColor = () => {
      const raw = localStorage.getItem('app.settings.primaryColor');
      const color = raw !== null ? JSON.parse(raw) : undefined;
      if (color) {
        const addStyles = () => {
          const styleElement = document.querySelector('#primaryColor');
          if (styleElement) {
            styleElement.remove();
          }
          const body = document.querySelector('body');
          const styleEl = document.createElement('style');
          const css = document.createTextNode(`:root { --kit-color-primary: ${color};}`);
          styleEl.setAttribute('id', 'primaryColor');
          styleEl.appendChild(css);
          body.appendChild(styleEl);
        };
        addStyles();
        this._settings.set({ primaryColor: color });
      }
    };
    primaryColor();
    await this.startApp();
  }

  setTheme = theme => {
    document.querySelector('html').setAttribute('data-kit-theme', theme);
    if (theme === 'default') {
      this._settings.set({ menuColor: 'gray' }); // 'light'
    }
    if (theme === 'dark') {
      this._settings.set({ menuColor: 'dark' });
    }
  };

  toggleTheme(): void {
    this.themeService.toggleTheme().catch(err => {
      this.logger.warn('[App] Error al cambiar tema:', err);
    });
  }

  async startApp() {
    this._eventManager.next(appStarted.name, null);
    // Bug 281127 — «atrás» tras cerrar sesión restauraba el dashboard desde el bfcache.
    this._restoredPageSession.start();
    this._maintenanceGuard.start();
    this.destroyRef.onDestroy(() => this._maintenanceGuard.stop());
    this._eventManager.subscribe(sessionToBeClosed.name, data => data && this.showSessionCloseWarning());
    this._eventManager.subscribe(sessionEnded.name, _event => {
      this.notificationEnabled = false;
      // Bug 266163 — el tracker conserva su propia copia del sessionId, así que
      // SESSION_ENDED/LOGOUT_EXECUTED salen con la sesión aunque el store del
      // UserSessionService ya esté limpio; se libera después de trackear.
      this.analyticsTracker.trackSessionEnded('LOGOUT');
      this.analyticsTracker.trackLogout();
      this.analyticsTracker.setSession(null);
      this._authService.logout();
      this._cdr.markForCheck();
    });
    this._eventManager.subscribe(sessionReplaced.name, () => this.showSessionReplacedNotification());
    this._eventManager.subscribe(sessionClosedByServer.name, () => this.showSessionClosedNotification());
    this._eventManager.subscribe(
      enableNotifications.name,
      event => (this.notificationEnabled = event?.enable ?? false)
    );
    this._eventManager.subscribe(invasiveNotification.name, notification => {
      this.notificationEnabled && this.showInvasiveNotifications(notification);
    });
  }

  closeEndSessionModelDialog() {
    this.inClosing = false;
    this.currentModal?.destroy();
    this.currentModal = null;
    this.autoCloseTimeout && clearTimeout(this.autoCloseTimeout);
    this.autoCloseTimeout = null;
  }

  showSessionCloseWarning() {
    if (this.inClosing) {
      return;
    }
    this.closeEndSessionModelDialog();
    this.inClosing = true;
    this.currentModal = this.modal.warning({
      nzTitle: 'Cierre de sesión por inactividad',
      nzContent: 'Su sesión está a punto de expirar, presione OK para mantener abierta la sesión',
      nzOnOk: () => {
        this._eventManager.next(keepTheSessionAlive.name, true);
        this.closeEndSessionModelDialog();
      },
    });
    this.autoCloseTimeout = setTimeout(() => this.closeEndSessionModelDialog(), sessionToBeClosedWarning * 0.8);
  }

  showSessionReplacedNotification() {
    // Sesión reemplazada por otra apertura del mismo usuario (otro dispositivo/navegador).
    // Avisamos con un mensaje específico y luego disparamos el cierre real (sessionEnded),
    // que ejecuta la limpieza y el logout normales. La sesión local ya no es válida, así que
    // cerramos sí o sí: al confirmar el usuario o por timeout si no responde.
    this.closeEndSessionModelDialog();
    this.notificationEnabled = false;
    let proceeded = false;
    const proceed = () => {
      if (proceeded) {
        return;
      }
      proceeded = true;
      this.autoCloseTimeout && clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
      this._eventManager.next(sessionEnded.name, null);
    };
    this.currentModal = this.modal.warning({
      nzTitle: 'Sesión cerrada',
      nzContent: 'Su sesión ha sido cerrada por detectarse un nuevo inicio de sesión.',
      nzOkText: 'Volver a ingresar',
      nzOnOk: () => proceed(),
    });
    this.autoCloseTimeout = setTimeout(() => proceed(), sessionToBeClosedWarning);
  }

  showSessionCloseNotification() {
    this.closeEndSessionModelDialog();
    this.currentModal = this.modal.warning({
      nzTitle: 'El tiempo de la sesión ha concluido',
      nzContent: 'Por tu seguridad, hemos cerrado tu sesión. Por favor, vuelve a ingresar',
    });
    this.autoCloseTimeout = setTimeout(() => this.closeEndSessionModelDialog(), sessionToBeClosedWarning);
  }

  showSessionClosedNotification() {
    // El backend reportó, en la respuesta de una petición, que la sesión se cerró/expiró
    // (marcador SESSION_CLOSED). Avisamos y luego disparamos el cierre real (sessionEnded), que
    // ejecuta la limpieza y el logout. La sesión ya no es válida, así que cerramos sí o sí: al
    // confirmar el usuario o por timeout si no responde. `proceeded` evita doble disparo.
    this.closeEndSessionModelDialog();
    this.notificationEnabled = false;
    let proceeded = false;
    const proceed = () => {
      if (proceeded) {
        return;
      }
      proceeded = true;
      this.autoCloseTimeout && clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
      this._eventManager.next(sessionEnded.name, null);
    };
    this.currentModal = this.modal.warning({
      nzTitle: 'Sesión cerrada',
      nzContent: 'Por tu seguridad, hemos cerrado tu sesión. Por favor, vuelve a ingresar.',
      nzOkText: 'Volver a ingresar',
      nzOnOk: () => proceed(),
    });
    this.autoCloseTimeout = setTimeout(() => proceed(), sessionToBeClosedWarning);
  }

  showInvasiveNotifications(notification) {
    const notificationId = this._userSession.getCustomSessionData('lastInvasiveNotification');
    if (notification?._id === notificationId) {
      return;
    }
    if (notification?._id) {
      if (!this.viewNotification) {
        const { resourceContent, callToAction = {} } = notification?.options ?? {};
        this.notification.id = notification?._id;
        this.notification.type = callToAction?.type;
        this.notification.action = callToAction?.detail;
        this.notification.image = resourceContent;
        this.viewNotification = this.notificationEnabled;
        this._userSession.addCustomSessionData('lastInvasiveNotification', notification?._id ?? null);
        setTimeout(() => this.closeNotification(), NOTIFICATION_VIEW_TIME);
      }
    } else {
      this._userSession.addCustomSessionData('lastInvasiveNotification', null);
    }
  }

  closeNotification() {
    this.viewNotification = false;
    this._eventManager.next(closeInvasiveNotification.name, {
      id: this.notification.id,
      permanent: false,
      nextForm: null,
    });
  }

  openNotification() {
    this.viewNotification = false;
    let nextForm = null;
    if (this.notification.type === 'LINK' && this.notification.action) {
      window.open(this.notification.action, '_external');
    } else if (this.notification.type === 'FORM' && this.notification.action) {
      nextForm = this.notification.action;
    }
    this._eventManager.next(closeInvasiveNotification.name, { id: this.notification.id, permanent: true, nextForm });
  }
}
